import { Ban, CalendarClock, CalendarRange, CheckCircle2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { AnnualProcurementPlanModal } from '../components/AnnualProcurementPlanModal';
import { PpmpRosterPanel } from '../components/PpmpRosterPanel';
import { annualProcurementPlansApi } from '../procurementPlansApi';
import type {
  AnnualProcurementPlanDetail,
  AppDepartmentRow,
} from '../procurementPlanTypes';
import { PPMP_PROGRESS } from '../procurementPlanTypes';

type TabKey = 'info' | 'departments';

/**
 * One annual procurement plan, and the roster of departments filing under it.
 *
 * **It LANDS ON DEPARTMENTS**, not Info — the twelfth member of the document-profile landing
 * family, and for the budget plan's own reason: a reader opens a procurement plan to read who
 * has filed and who has not, which is its CONTENT, rather than to check its three dates.
 * `Info` is still the first tab DECLARED, so the strip's order and the dev check are untouched.
 */
export function AnnualProcurementPlanProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [plan, setPlan] = useState<AnnualProcurementPlanDetail | null>(null);
  const [roster, setRoster] = useState<AppDepartmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [running, setRunning] = useState(false);

  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [tab, setTab] = useState<TabKey>('departments');

  /*
    The plan AND its roster, because the page needs both and neither is derivable from the
    other: the plan carries the window the roster's progress is derived against, and the roster
    is the only thing that can name a department which has filed NOTHING.
  */
  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      Promise.all([
        annualProcurementPlansApi.get(id, signal),
        annualProcurementPlansApi.roster(id, signal),
      ])
        .then(([nextPlan, nextRoster]) => {
          if (signal?.aborted) return;

          setPlan(nextPlan);
          setRoster(nextRoster);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError('Could not load this annual procurement plan.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function run(action: 'delete' | 'restore') {
    if (!id) return;

    setRunning(true);
    setError(null);

    try {
      if (action === 'delete') await annualProcurementPlansApi.deactivate(id);
      else await annualProcurementPlansApi.activate(id);

      setConfirmingDelete(false);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Procurement Planning',
    {
      label: 'Annual Procurement Plan',
      to: '/procurement/procurement-planning/annual-procurement-plans',
    },
    plan?.appNumber ?? 'Annual procurement plan',
  ];

  // A FAILED REFRESH must not replace the page — guarded on `error && !plan`, never `error`
  // alone, or a transient failure blanks everything the reader was looking at.
  if (error && !plan) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading && !plan) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!plan) return null;

  const started = roster.filter((row) => row.progress !== PPMP_PROGRESS.notStarted).length;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="annual-procurement-plan-profile"
        header={
          <ProfileHeader
            id="annual-procurement-plan-profile-header"
            icon={<CalendarRange className="h-6 w-6" aria-hidden="true" />}
            title={plan.appNumber}
            meta={[
              `FY ${plan.fiscalYear}`,
              plan.budgetPlanNumber,
              `${formatDateOnly(plan.coverageStartDate)} – ${formatDateOnly(plan.coverageEndDate)}`,
            ]}
            /*
              The two figures a reader opens a plan to find, on the second line because the
              first is already carrying three. Derived from the roster the page already holds —
              fetching them again would be two answers to one question.
            */
            secondary={[
              `${started}/${roster.length} departments started`,
              `${formatMoney(plan.estimatedBudget)} planned`,
            ]}
            status={
              plan.isActive ? (
                <StatusText tone="active">Active</StatusText>
              ) : (
                <StatusText tone="inactive">Deleted</StatusText>
              )
            }
            settings={{
              onEdit: () => setEditing(true),
              editPermission: 'annual-procurement-plans.update',
              onHistory: () => setHistory(true),
              onDelete: plan.isActive ? () => setConfirmingDelete(true) : undefined,
              deletePermission: 'annual-procurement-plans.delete',
            }}
            createdBy={plan.createdByName}
            createdAt={plan.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="annual-procurement-plan-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : !plan.isActive ? (
            <ProfileBanner
              id="annual-procurement-plan-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={`${plan.appNumber} is deleted. Its PPMPs are kept exactly as they are, and ${plan.budgetPlanNumber} is free for another procurement plan.`}
              action={
                <PermissionGate permission="annual-procurement-plans.delete">
                  <Button
                    id="annual-procurement-plan-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void run('restore')}
                    disabled={running}
                    loading={running}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : plan.isSubmissionOpen ? (
            <ProfileBanner
              id="annual-procurement-plan-profile-banner"
              tone="slate"
              icon={<CalendarClock className="h-4 w-4" aria-hidden="true" />}
              message={`Departments may file their PPMPs until ${formatDateOnly(plan.submissionClosesAt)}. ${roster.length - started} of ${roster.length} have not started.`}
            />
          ) : (
            /*
              AMBER rather than red: a closed window is not a fault, it is the plan having moved
              on — and it is what a reader needs to know before wondering why Create is
              disabled on the roster.
            */
            <ProfileBanner
              id="annual-procurement-plan-profile-banner"
              tone="amber"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={`The submission window closed on ${formatDateOnly(plan.submissionClosesAt)}. No further PPMP can be filed under this plan — correct the deadline if departments are still expected to file.`}
            />
          )
        }
        /*
          `Info` is the FIRST tab DECLARED, which is what the development check asserts; the
          page merely LANDS on Departments.
        */
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'departments',
            label: (
              <>
                Departments <CountBadge count={roster.length} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
        panel={tab === 'departments' ? 'bleed' : 'cards'}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="annual-procurement-plan-profile-frame"
              title="Plan"
              onEdit={() => setEditing(true)}
              editPermission="annual-procurement-plans.update"
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Plan number', value: plan.appNumber, mono: true },
                  { label: 'Budget plan', value: plan.budgetPlanNumber, mono: true },
                  { label: 'Fiscal year', value: String(plan.fiscalYear), mono: true },
                  {
                    label: 'Submission by',
                    value: formatDateOnly(plan.submissionClosesAt),
                    mono: true,
                  },
                  {
                    label: 'Coverage from',
                    value: formatDateOnly(plan.coverageStartDate),
                    mono: true,
                  },
                  { label: 'Coverage to', value: formatDateOnly(plan.coverageEndDate), mono: true },
                  { label: 'Description', value: plan.description },
                  { label: 'Notes', value: plan.notes },
                ]}
              />
            </ProfileCard>

            {/*
              A DERIVED VIEW of figures the roster already states — the fourth case in
              `<ProfileCard>`'s no-Edit list. Nothing here is a field: the way to move any of
              them is to file a PPMP, which happens on the Departments tab.
            */}
            <ProfileCard id="annual-procurement-plan-profile-roll-up" title="Filing">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Departments enrolled', value: String(roster.length), mono: true },
                  { label: 'Departments started', value: String(started), mono: true },
                  { label: 'PPMPs filed', value: String(plan.ppmpCount), mono: true },
                  {
                    label: 'Estimated budget',
                    value: formatMoney(plan.estimatedBudget),
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="annual-procurement-plan-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: plan.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(plan.createdAt)} className="font-mono">
                        {formatDateTime(plan.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: plan.updatedByName },
                  {
                    label: 'Updated',
                    value: plan.updatedAt ? (
                      <time dateTime={toIso(plan.updatedAt)} className="font-mono">
                        {formatDateTime(plan.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          /* A `bleed` panel owes its child a `p-4` wrapper, or the search group's border sits
             on the panel's and the two read as one control that has come apart. */
          <div className="flex h-full min-w-0 flex-col p-4">
            <PpmpRosterPanel plan={plan} roster={roster} onChanged={() => load()} />
          </div>
        )}
      </ProfileHeaderShell>

      {editing ? (
        <AnnualProcurementPlanModal
          open
          editing={plan}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {history ? (
        <HistoryLogsModal
          open
          onClose={() => setHistory(false)}
          module="annual-procurement-plans"
          entityId={plan.id}
        />
      ) : null}

      {confirmingDelete ? (
        <ConfirmDialog
          open
          onClose={() => setConfirmingDelete(false)}
          onConfirm={() => void run('delete')}
          title="Delete annual procurement plan"
          danger
          requireConfirm
          requireCredential
          loading={running}
          id="annual-procurement-plan-profile-delete-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {plan.appNumber} is hidden from the register, and {plan.budgetPlanNumber} becomes
                free for another procurement plan.
              </p>
              <p>
                {plan.ppmpCount === 0
                  ? 'Nothing has been filed under it.'
                  : `The ${plan.ppmpCount} PPMP${plan.ppmpCount === 1 ? '' : 's'} filed under it are kept exactly as they are, so a restore brings the plan back with its contents.`}{' '}
                The plan number is never reused.
              </p>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
